<template>
	<div class="component-section" @click="handleClick" data-no-color-picker="true">
		<h3 class="section-title">Selection Components</h3>
		<div class="selection-grid">
			<div class="form-field">
				<label>Select</label>
				<Select v-model="selectedOption" :options="dropdownOptions" optionLabel="name" placeholder="Select..." disabled />
			</div>
			<div class="form-field">
				<label>MultiSelect</label>
				<div class="multiselect-preview">
					<MultiSelect v-model="selectedItems" :options="multiselectOptions" optionLabel="name" placeholder="Choose items" disabled />
					<div class="multiselect-dropdown">
						<div class="multiselect-option selected">Item 1</div>
						<div class="multiselect-option selected">Item 2</div>
						<div class="multiselect-option">Item 3</div>
						<div class="multiselect-option">Item 4</div>
					</div>
				</div>
			</div>
			<div class="form-field">
				<label>MultiSelect with Chips</label>
				<div class="multiselect-preview">
					<div class="multiselect-with-chips">
						<Chip label="Technology" removable />
						<Chip label="Design" removable />
						<i class="pi pi-chevron-down multiselect-trigger"></i>
					</div>
					<div class="multiselect-dropdown">
						<div class="multiselect-option selected">Technology</div>
						<div class="multiselect-option selected">Design</div>
						<div class="multiselect-option">Development</div>
						<div class="multiselect-option">Testing</div>
						<div class="multiselect-option">Marketing</div>
					</div>
				</div>
			</div>
			<div class="form-field">
				<label>Standalone Chip</label>
				<div class="chip-container">
					<Chip label="Sample Chip" removable />
				</div>
			</div>
			<div class="form-field">
				<label>Checkbox</label>
				<div class="flex gap-4">
					<Checkbox v-model="checkedTrue" binary disabled class="checkbox-primary" />
					<Checkbox v-model="checkedFalse" binary disabled class="checkbox-secondary" />
				</div>
			</div>
			<div class="form-field">
				<label>RadioButton</label>
				<div class="flex gap-4">
					<div class="flex items-center gap-1">
						<RadioButton v-model="selectedRadio" inputId="option1" value="Option1" disabled class="radiobutton-primary" />
						<label for="option1">Option 1</label>
					</div>
					<div class="flex items-center gap-1">
						<RadioButton v-model="selectedRadio" inputId="option2" value="Option2" disabled class="radiobutton-secondary" />
						<label for="option2">Option 2</label>
					</div>
				</div>
			</div>
			<div class="form-field">
				<label>ToggleSwitch</label>
				<div class="flex gap-4">
					<ToggleSwitch v-model="toggleSwitchOn" style="pointer-events: auto" />
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import Select from "primevue/select"
import MultiSelect from "primevue/multiselect"
import Chip from "primevue/chip"
import Checkbox from "primevue/checkbox"
import RadioButton from "primevue/radiobutton"
import ToggleSwitch from "primevue/toggleswitch"

const emit = defineEmits<{
	(e: "elementClick", event: MouseEvent): void
}>()

const selectedOption = ref()
const selectedItems = ref()
const selectedChipItems = ref()
const checkedTrue = ref(true)
const checkedFalse = ref(false)
const selectedRadio = ref("Option1")
const toggleSwitchOn = ref(true)

const dropdownOptions = [
	{ name: "Option 1", code: "opt1" },
	{ name: "Option 2", code: "opt2" },
	{ name: "Option 3", code: "opt3" },
]

const multiselectOptions = [
	{ name: "Item 1", code: "item1" },
	{ name: "Item 2", code: "item2" },
	{ name: "Item 3", code: "item3" },
	{ name: "Item 4", code: "item4" },
]

const chipOptions = [
	{ name: "Technology", code: "tech" },
	{ name: "Design", code: "design" },
	{ name: "Development", code: "dev" },
	{ name: "Testing", code: "test" },
	{ name: "Marketing", code: "marketing" },
]

onMounted(() => {
	selectedOption.value = dropdownOptions[0]
	selectedItems.value = [multiselectOptions[0], multiselectOptions[1]]
	selectedChipItems.value = [chipOptions[0], chipOptions[1]]
})

const handleClick = (event: MouseEvent) => {
	emit("elementClick", event)
}
</script>

<style lang="scss" scoped>
.component-section {
	background: white;
	border-radius: 8px;
	padding: 1.5rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	margin-bottom: 1rem;
}

.section-title {
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 1rem;
	color: #374151;
	border-bottom: 2px solid #e5e7eb;
	padding-bottom: 0.5rem;
}

.selection-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 1rem;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.form-field label {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6b7280;
}

.flex {
	display: flex;
}

.gap-2 {
	gap: 0.5rem;
}

.gap-4 {
	gap: 1rem;
}

.gap-1 {
	gap: 0.25rem;
}

.items-center {
	align-items: center;
}

.chip-container {
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
}

/* Style disabled components to indicate they're for color modification only */
.form-field :deep(.p-disabled) {
	opacity: 1 !important;
	pointer-events: auto !important;
	cursor: pointer !important;
}

.form-field :deep(.p-select.p-disabled),
.form-field :deep(.p-multiselect.p-disabled) {
	background: #f8f9fa !important;
	border-color: #dee2e6 !important;
}

.form-field :deep(.p-checkbox.p-disabled),
.form-field :deep(.p-radiobutton.p-disabled),
.form-field :deep(.p-toggleswitch) {
	opacity: 1 !important;
	pointer-events: auto !important;
	cursor: pointer !important;
}

/* MultiSelect Preview Styling */
.multiselect-preview {
	display: flex;
	flex-direction: column;
	width: 100%;
}

.multiselect-dropdown {
	position: static;
	background: var(--p-multiselect-dropdown-background, white);
	border: 1px solid var(--p-multiselect-dropdown-border-color, #d1d5db);
	border-radius: var(--p-multiselect-dropdown-border-radius, 6px);
	box-shadow: var(--p-multiselect-dropdown-shadow, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
	margin-top: 4px;
	max-height: 150px;
	overflow-y: auto;
}

.multiselect-option {
	padding: 0.75rem;
	border-bottom: 1px solid var(--p-multiselect-option-border-color, #f3f4f6);
	cursor: pointer;
	font-size: 0.875rem;
	transition: background-color 0.2s;
}

.multiselect-option:hover {
	background: var(--p-multiselect-option-hover-background, #f3f4f6);
}

.multiselect-option.selected {
	background: var(--p-multiselect-option-selected-background, #3b82f6);
	color: var(--p-multiselect-option-selected-color, white);
}

.multiselect-option:last-child {
	border-bottom: none;
}

/* MultiSelect with Chips styling */
.multiselect-with-chips {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.75rem;
	background: var(--p-multiselect-background, white);
	border: 1px solid var(--p-multiselect-border-color, #d1d5db);
	border-radius: var(--p-multiselect-border-radius, 6px);
	flex-wrap: wrap;
}

.multiselect-with-chips .p-chip {
	pointer-events: auto;
	cursor: pointer;
}

.multiselect-trigger {
	margin-left: auto;
	color: var(--p-multiselect-trigger-color, #6b7280);
	font-size: 0.875rem;
	pointer-events: none;
}
</style>
